/**
 * Release notes — the single source for "What's new".
 *
 * Shown once after the app updates itself, and kept on More → What's new (the
 * avatar menu on desktop) so an agent can read it again whenever they like.
 *
 * Newest first, and the top entry's `version` MUST match package.json: the sheet
 * works out what is unseen by where the agent's last-seen version sits in THIS
 * list, so a release that ships without an entry here announces nothing.
 *
 * Agent-facing copy — what changed for them, not what changed in the code. One
 * line each: this is read standing up, between viewings.
 *
 * An item may carry `videos: [{ src, poster, label }]` — a demo clip under the
 * note. The files live in public/whats-new/ and are NOT precached (the Workbox
 * globs leave out .mp4/.jpg), so a clip costs nothing until someone presses play.
 * Only clips with demo people in them belong here: every agent sees these notes,
 * so a recording signed in as a real agent, or showing one's figures, stays out.
 */
export const RELEASES = [
  {
    version: '1.21.0',
    date: '2 Nov 2026',
    items: [
      {
        title: 'Fix a landlord’s or tenant’s details yourself',
        body: 'Saved a landlord, tenant or owner with a wrong name or a missing registration number? On a booking form, Edit details next to Change corrects the contact, and the form picks up the new name and number. In Contacts, you can now edit any landlord or tenant on your own sales and booking forms, not only the ones you registered. Forms already sent for signing or issued keep what was signed. Bank accounts stay with whoever registered the contact.',
      },
      {
        title: 'Change the landlord or tenant on a converted sale',
        body: 'On a converted sale, the Parties tab now saves a change of landlord or tenant. Someone who has an invoice made out to them cannot be removed; HQ is told about the change.',
      },
      {
        title: 'See each co-sharing agent’s branch',
        body: 'The agents and commission split on a sale now shows which branch each agent is from, with an Other branch tag on agents outside your branch, so a cross-branch co-share is easy to spot.',
      },
    ],
  },
  {
    version: '1.20.0',
    date: '28 Oct 2026',
    items: [
      {
        title: 'Centre Directors: your branch’s sales',
        body: 'If HQ has made you the Centre Director of a branch, the Sales tab now has a switch: My sales, and your branch. Your branch lists every sale in it, with the agents on each deal. Pick an agent to see only their deals, filter by stage, deal type or date, and search by client, property, reference, case number or form number. Open a sale to see where it stands, who is on it, the parties, the price, how the commission splits and how much of the fee the client has paid. It is read only: receipts and comments stay with the agents on the deal. Every other agent sees their own sales, as before.',
        videos: [
          { src: '/whats-new/branch-sales.mp4', poster: '/whats-new/branch-sales.jpg', label: 'A Centre Director’s branch sales, on the phone and on a computer' },
        ],
      },
      {
        title: 'New tutorial: what you earn on every sale',
        body: 'More → Tutorials has a short video on Earnings: your commission on each sale, what is released and paid, and every payout with its payment voucher.',
      },
      {
        title: 'Rental deals: the split adds up',
        body: 'On a rental deal, your share of the tenant’s stamping fee now shows under its own heading, Tenancy fee, instead of being counted as one of the people the commission is split between.',
      },
    ],
  },
  {
    version: '1.19.0',
    date: '26 Oct 2026',
    items: [
      {
        title: 'Project brochures, price lists and videos',
        body: 'More → Projects (Projects in the menu on desktop) lists every project HQ has uploaded materials for. Open one to see its brochure, price list, floor plans, photos and videos, by category. Photos, floor plans and videos open in one viewer you swipe through, and videos play right in the app. PDFs open in the app; Word, Excel and PowerPoint files download. Share sends the file itself to WhatsApp or any app; when your phone can only share a link, that link opens for 1 hour. A project listing shows Project materials when its project has some.',
        videos: [
          { src: '/whats-new/project-materials.mp4', poster: '/whats-new/project-materials.jpg', label: 'HQ uploads the materials, then viewing, playing, sharing and downloading them in CIDAS' },
        ],
      },
      {
        title: 'Project sale: type the unit if it isn’t in the list',
        body: 'When a project has no units uploaded, or yours is not in the list, type the unit into the property address and key in the price. The commission rate and SST still come from the project.',
        videos: [
          { src: '/whats-new/project-sale-typed-unit.mp4', poster: '/whats-new/project-sale-typed-unit.jpg', label: 'Keying in a project sale with the unit typed in the address' },
        ],
      },
      {
        title: 'Videos on events',
        body: 'An event’s photos can now include videos. Tap play on the event page and it plays in the app.',
        videos: [
          { src: '/whats-new/event-videos.mp4', poster: '/whats-new/event-videos.jpg', label: 'A video in an event’s gallery, played in CIDAS' },
        ],
      },
    ],
  },
  {
    version: '1.18.0',
    date: '23 Oct 2026',
    items: [
      {
        title: 'Memos from HQ',
        body: 'HQ can now send you a memo in CIDAS. An important one pops up over whatever screen you are on and stays until you tap I have read this, so read it through first; pictures open full size and a PDF opens in the app. HQ sees when you confirmed. Memos also show in your notifications, and More → Memos keeps every memo you have had.',
      },
      {
        title: 'Co-share form: when the sale has changed, reissue it',
        body: 'If a sale changes after its internal co-share form went out — an agent added, taken off or swapped, a share changed, or a different fee or SST — the form now lists exactly what changed. It can no longer be signed; tap Reissue with the sale as it is now and everyone signs the new version. Every agent still on the sale sees it on Home under Needs you. If you were taken off the sale, you are no longer asked to sign.',
      },
    ],
  },
  {
    version: '1.17.0',
    date: '16 Oct 2026',
    items: [
      {
        title: 'Change a booking form after it has gone out',
        body: 'No more cancelling a form and typing it again. Out for signature or signed, not issued yet? Tap Edit at the top of the form: it goes back to a draft under the same Ref No. and LOU number, and everyone signs again. Already issued? Tap Revise: you get a new form, copied from it, with its own number and LOU number. The old form stays in force until you issue the new one, then it is cancelled as revised and your listing stays under offer. Once a form has become a sale, change the sale instead.',
        videos: [
          { src: '/whats-new/change-form-after-it-goes-out.mp4', poster: '/whats-new/change-form-after-it-goes-out.jpg', label: 'Editing a form out for signature, revising an issued one, and reprinting a Letter of Undertaking' },
        ],
      },
      {
        title: 'Reprint a Letter of Undertaking under the same number',
        body: 'Every CID Forms letter you print is now saved. Open it again from Printed by you, on any device, change it and print it: it keeps the same number.',
      },
    ],
  },
  {
    version: '1.16.0',
    date: '14 Oct 2026',
    items: [
      {
        title: 'Your payouts and their payment vouchers',
        body: 'See every payout and open its payment voucher in Earnings. Under the payout ledger, each payout shows its date, amount and voucher number; tap it to see which sales it paid and to open the voucher, then share or save it. When HQ attaches or replaces a voucher you get a notification that opens it. Your commission statement lists the voucher number beside each disbursement too.',
        videos: [
          { src: '/whats-new/payment-voucher.mp4', poster: '/whats-new/payment-voucher.jpg', label: 'Your payouts and their vouchers, on the phone and on a computer' },
        ],
      },
      {
        title: 'Check in to an event from a screenshot',
        body: 'On a Zoom event with only one device? Take a screenshot of the event QR, then on Scan event QR tap Choose from gallery and pick it. Use it straight away: the code changes every minute.',
      },
      {
        title: 'Letters of Undertaking: the new wording',
        body: 'The Vendor’s LOU now says the vendor’s solicitors release the amount once the property consent is obtained, and the Vendor’s and Landlord’s LOUs show your branch and its licence number under TO:. On every LOU the amount box leaves room to write the fee by hand, and a fee not filled in yet prints as a blank line.',
      },
      {
        title: 'Smaller things',
        body: 'On a co-agency letter, clause 2a) now prints only the selling or the rental wording, whichever the letter is for.',
      },
    ],
  },
  {
    version: '1.15.0',
    date: '10 Oct 2026',
    items: [
      {
        title: 'Change the co-agency, referral or split after the invoice',
        body: 'Missed a referral, or a co-agency’s share is wrong, on an invoiced sale? Tap Edit: until the sale is completed you can still add, change or remove a co-agency or referral, and change the agent split. Before you save, you see how your own commission and the co-agency and referral lines change, and you give a reason. HQ finance is told, and the commission is worked out again at the rates locked in at the first invoice. A change that would move money already split from the buyer’s payment, or already paid out, waits until HQ puts it back. You cannot take yourself off a sale, and you are notified when you are added to one or your split changes.',
        videos: [
          { src: '/whats-new/change-parties-after-invoice.mp4', poster: '/whats-new/change-parties-after-invoice.jpg', label: 'Changing a referral, a split and a co-agency on an invoiced sale' },
        ],
      },
    ],
  },
  {
    version: '1.14.0',
    date: '8 Oct 2026',
    items: [
      {
        title: 'Your documents from the old system',
        body: 'Your application form, IC copy, certificates and other documents from the old system are now in Profile → My Documents. You can also add any document or image there, including Word files, up to 25 MB.',
      },
      {
        title: 'Clearer printed documents',
        body: 'Agreements, LOUs, letters and your commission statement now print in one typeface with slightly larger text and the same margin on every page. A one-page form stays on one page.',
      },
      {
        title: 'Smaller things',
        body: 'Documents on your older sales now open inside CIDAS, like any other document, instead of sending you to the browser.',
      },
    ],
  },
  {
    version: '1.13.0',
    date: '8 Oct 2026',
    items: [
      {
        title: 'Choose what Print / PDF prints',
        body: 'Print / PDF on your Agreement to Purchase or Rent asks which documents to print. The owner’s Letter of Undertaking starts unticked, so the buyer’s or tenant’s copy does not show CID’s fee to the owner. On a draft, you can switch the LOU off when the deal does not need one.',
        videos: [
          { src: '/whats-new/print-pick-documents.mp4', poster: '/whats-new/print-pick-documents.jpg', label: 'Choosing what prints, and switching the LOU off' },
        ],
      },
      {
        title: 'Correct your sale after the invoice',
        body: 'Price wrong on an invoiced sale? Tap Edit: you can still correct the figures until the sale is completed. HQ finance is told, and your commission follows the new figures at the rates locked in at the first invoice, unless HQ finance has set it by hand.',
      },
      {
        title: 'Smaller things',
        body: 'On Earnings, a deal where you earn only an override now opens your own lines instead of “Sale not found”. If HQ reverses a payout, you get a notification with the reason, and your earnings stop counting it as paid.',
      },
    ],
  },
  {
    version: '1.12.0',
    date: '5 Oct 2026',
    items: [
      {
        title: 'Event posters and photos',
        body: 'HQ’s events now show their poster on the event card and at the top of the event. Tap it to see it full size, and flip through the event’s photos.',
        videos: [
          { src: '/whats-new/event-poster-photos.mp4', poster: '/whats-new/event-poster-photos.jpg', label: 'Event posters and photos' },
        ],
      },
    ],
  },
  {
    version: '1.11.0',
    date: '4 Oct 2026',
    items: [
      {
        title: 'Inventory List and the owner’s LOU, signed with the agreement',
        body: 'Your Agreement to Purchase or Agreement to Rent now carries the owner’s Letter of Undertaking, and one switch adds an Inventory List. The list is yours to write: start from CID’s standard rooms or a blank list, add or rename rooms, and list each item with quantity, brand and, if you like, condition and a note. On the LOU you only enter your professional fee, its SST and any disbursements; the rest comes from the agreement, and the LOU gets its own VLU / LLU number. The buyer or tenant and the owner open and tick each document as read, then sign once for all of them; a second owner signs the LOU too. Printing for wet-ink signing? Print / PDF numbers the LOU first. In CID Forms, both Inventory Lists can now be filled in, and the LOUs and Inventory Lists have Fill from an OTP / OTR.',
        videos: [
          { src: '/whats-new/inventory-list-lou.mp4', poster: '/whats-new/inventory-list-lou.jpg', label: 'The Inventory List and the owner’s LOU, from the agreement to the signatures' },
        ],
      },
      {
        title: 'Save a draft any time — your typing is kept',
        body: 'A booking form saves as a draft before you have the buyer, owner or price; what is still missing is listed when you send it for signing or issue it. Booking forms, co-agency letters and listings save as you type, the sale form keeps what you typed on your phone if the app reloads, and every form asks before you leave with unsaved changes.',
      },
      {
        title: 'Letters of Undertaking: fee as % or a fixed RM amount',
        body: 'In CID Forms, the Vendor’s and the Purchaser’s Letters of Undertaking let you set the professional fee as a % of the price or as a fixed RM amount. A fixed fee prints as the ringgit amount, with no percentage.',
        videos: [
          { src: '/whats-new/lou-rm-fee.mp4', poster: '/whats-new/lou-rm-fee.jpg', label: 'Professional fee as % or RM' },
        ],
      },
      {
        title: 'Case no. and ATP / ATR no. on your sale',
        body: 'A sale now shows HQ’s Case no. and the number of the Agreement to Purchase or Rent it came from, and you can search your sales by either.',
      },
      {
        title: 'Once HQ has your claim, the stage is HQ’s',
        body: 'Once a sale is at Documents Completed, HQ Verified, HQ Approved or Paid, only HQ can change its stage, so the Change button is gone. You can still add documents, receipts and notes. If HQ rejects the claim, the stage is yours again to fix it and claim again.',
      },
      {
        title: 'Smaller things',
        body: 'When an owner, landlord, buyer or tenant is a company, booking forms label their number “Co. Reg. No” instead of NRIC No. The Authorisation to Let and Exclusive Letting letters ask whether the rental is exclusive of SST (the default) or inclusive at a rate you give. Co-agency letters print on one A4 page with nothing cut off. The Agreement to Rent’s Commencement Date shows only the start date.',
      },
    ],
  },
  {
    version: '1.10.0',
    date: '4 Oct 2026',
    items: [
      {
        title: 'Events: trainings and events from HQ, in the app',
        body: 'More → Events (Events in the sidebar on a computer) shows HQ’s trainings, events and launches by week, each with its time, speaker and venue, coloured by type: blue for Training, purple for Event, red for Important Training, teal for Project Launch. Open one to register — with one tap, or with a short form where HQ asks something (number of pax, meal, …). Your name, branch, team leader and phone go with it from your profile, so there is nothing to retype. A full event offers its waitlist, and you are told the moment a seat opens. My events lists everything you have joined.',
        videos: [
          { src: '/whats-new/events-module.mp4', poster: '/whats-new/events-module.jpg', label: 'Events: from HQ’s calendar to registering and checking in at the door' },
        ],
      },
      {
        title: 'Your ticket, and checking in at the door',
        body: 'Once registered, My ticket shows your personal QR: show it at the door and staff scan it. Or check yourself in: tap Scan event QR and point your phone at the code on the screen at the entrance — it changes every minute, so a photo of it soon stops working. For a paid event you see the fee for your pax; HQ confirms your payment (bank-in or at the desk) and the app tells you when it is done; until then, staff check you in at the desk. Turned up without registering? Scan the code anyway: if the event has questions, you answer them on the spot and you are checked in.',
      },
      {
        title: 'Dietary restrictions on your profile',
        body: 'Profile has a Dietary restrictions box (Vegetarian, Halal, No beef, …). It fills in by itself when you register for an event; you can change it for one event without changing your profile.',
      },
    ],
  },
  {
    version: '1.9.0',
    date: '29 Sep 2026',
    items: [
      {
        title: 'Each side fills in and signs their own details',
        body: 'The Agreement to Purchase and the Agreement to Rent no longer need both parties typed in before you can go ahead. Leave the buyer or the owner blank and use Fill & sign on the form’s page to send that side a link: they type their own name and IC, read the agreement, sign, and it comes back to you. The first side to sign does not see the other side’s details; the second side sees the offer they are accepting. Where a deal needs both kept private, switch on “Keep both sides hidden from each other” — you still see the complete agreement.',
        videos: [
          { src: '/whats-new/fill-and-sign.mp4', poster: '/whats-new/fill-and-sign.jpg', label: 'Fill & sign, one side at a time' },
          { src: '/whats-new/fill-and-sign-both-hidden.mp4', poster: '/whats-new/fill-and-sign-both-hidden.jpg', label: 'Keeping both sides hidden' },
        ],
      },
      {
        title: 'Your year, month by month',
        body: 'Earnings has a Total earning block: a year of your own commission and your override income side by side, with a bar for each month. Pick a year, tap a month to read its two figures, or open Figures for the whole twelve months as a table. It counts what you earned on deals dated in each month — what has been released and what has been paid are still on the tiles above it.',
      },
      {
        title: 'Your own booking form number on a case',
        body: 'When you submit a case there is a new “Your booking form no. (optional)” box for the number printed on your own booking form pad, so the paper and the case can be matched. It shows on the sale, you can search your sales by it, and HQ sees the same number.',
      },
      {
        title: 'SST on the Landlord’s Letter of Undertaking is your choice',
        body: 'The letter has a new “SST on the professional fees” choice. It starts at “Charged to the landlord (8%)”, exactly as before; pick “Absorbed by the agent — not charged” and the SST line, the total and the “plus 8% SST” wording in the clauses all come out together. The Vendor’s and Purchaser’s letters are unchanged.',
      },
      {
        title: 'A referral can be paid on what CID keeps after the co-agency',
        body: 'On a referral row, “Referral % of” now offers “After co-agency” as well as “CID’s portion”. On a We-list deal shared with a co-agency, the referrer’s percentage is then worked out on what is left after the co-agency is paid, not on the whole fee. Existing sales keep the old rule until someone changes it.',
      },
      {
        title: 'Lines HQ adds by hand',
        body: 'HQ can now add a commission line to a sale by hand — for example a disbursement that carries no SST and is yours in full. It shows on the sale’s Commissions tab and on Earnings as one of your own lines, marked “Added by HQ”, and counts in what you are owed.',
      },
      {
        title: 'Smaller things',
        body: 'The nationality list now says “Chinese (China)” for a Chinese national, so typing “china” finds it.',
      },
    ],
  },
  {
    version: '1.8.1',
    date: '11 Sep 2026',
    items: [
      {
        title: 'Search-and-add lists now open properly on the desktop app',
        body: 'On some Windows computers the search-and-add lists — landlord, tenant, vendor, purchaser, co-agency and the rest — were opening where you could not see them, and the screen could flicker when you clicked a field, so it looked as though nothing happened. They now open right under the field every time, with the box you type in to filter it clearly in view.',
      },
    ],
  },
  {
    version: '1.8.0',
    date: '7 Sep 2026',
    items: [
      {
        title: 'Listings for shops, factories and land',
        body: 'Property type on a listing now runs across four tabs — Residential, Commercial, Industrial and Land — with the types the office has always used on sales: shop office, link factory, warehouse, agricultural land and so on. Pick Land and the form stops asking for bedrooms, built-up and furnishing, and shows the area in acres beside the square feet. Listings you have already saved keep their type.',
      },
      {
        title: 'Exclusive appointments are in the app',
        body: 'The Exclusive Authorisation to Sell and to Let now sit alongside the general ones as ES and EL, numbered in their own series. They print what the exclusive pad prints — an exclusive period rather than an agency basis, the fee stated as a figure, and the clause that entitles CID to its fee even where the owner finds the buyer or tenant themselves. Everything after is unchanged: sign it, issue it, and back a listing with it exactly as you would an ALS or ALR.',
      },
      {
        title: 'Smaller things',
        body: 'On the documents the app prints, the estate agency licence number has moved out of the company name line and now sits under the licence mark on the right — “Ejen Harta Tanah” then the number — the way the printed pads have always had it.',
      },
    ],
  },
  {
    version: '1.7.0',
    date: '3 Sep 2026',
    items: [
      {
        title: 'Your headline figure is commission you have been paid',
        body: 'Home now leads with GCI collected this year — commission the office has actually received and knocked off against an invoice. It is the same figure as your Earnings tab, the leaderboard and the office, and the one your tier moves on. It replaces “GCI this month”, which counted what you had closed rather than what had come in; the two could sit far apart. “Closed this month” is still there, as a count of deals.',
      },
      {
        title: 'Any money on a booking form can be a flat sum',
        body: 'Earnest deposit, balance deposit, balance of the purchase price, security and utility deposits, advance rental — each now takes either the share it always did or a fixed RM amount, chosen per figure. A deal struck as “RM 5,000, full stop” no longer has to be written as a percentage that re-prices itself when the price is corrected. Payment lines you add on an authorisation to let carry over into the offer to rent.',
      },
      {
        title: 'New form: Delivery of Vacant Possession',
        body: 'The handover form is now in Documents and you fill it in the app: meter readings for electricity, water and gas, and the keys, access cards and anything else handed over. Both sides sign on your phone, and it prints on your branch’s letterhead with its own reference number.',
      },
      {
        title: 'Blank copies come from Print / save',
        body: 'A fillable document no longer offers “Download blank form”. Open it, print it with nothing filled in, and you get the same blank form — but on your own branch’s letterhead and at the current SST rate, both of which the old stored copy had wrong.',
      },
      {
        title: 'Team performance fits a tablet',
        body: 'On a narrower screen the roster now gets the full width instead of squeezing names down to initials, the exception queue moves below it, and the tiles across the top stop wrapping. A dormant member also says why — their last closing, or that there is none on record.',
      },
      {
        title: 'Smaller things',
        body: 'The documents the app builds itself — the co-agency letter and your statement — print the same full letterhead as everything the office issues. And on a General Authorisation to Sell the interest rate prints with its % sign, and the payment period in words, the way the printed form reads.',
      },
    ],
  },
  {
    version: '1.6.0',
    date: '21 Aug 2026',
    items: [
      {
        title: 'Ask HQ for the customer’s receipt',
        body: 'Request receipt joins your run of stages on a sale: once the payment is in, ask HQ to issue the customer their official receipt. Requesting an invoice and saying a deal is ready to claim now reach the office too — before this they moved the stage and told nobody.',
      },
      {
        title: 'The official receipt is in the app',
        body: 'Each invoice on a sale now lists the official receipts raised against it, with the document to view, share or print — the paper the customer comes back to you for. One receipted from a branch’s own book says so, and says when the document is still to come.',
      },
      {
        title: 'A “They list” deal records the whole deal',
        body: 'Where the other agency holds the listing, key the full fee the way you would key any deal and state CID’s portion of it — a percentage or a flat RM sum. That portion is what your split, the royalty and your GCI all run on, and the sale list shows it as “of RM x · they list”.',
      },
      {
        title: 'A co-agency share can be a flat sum',
        body: 'On a sale and on a co-agency letter you can now agree a fixed RM amount — “RM 5,000 to them” — instead of a percentage of the fee. CID takes the balance of whatever is received, so it stays right if the fee is later corrected. Letters issued before this are untouched.',
      },
      {
        title: 'Balance To, or Refund',
        body: 'The Balance tab is now Balance / Refund, and each entry says which it is: a balance passed on to whoever the money belongs to, or a refund going back to whoever paid it in. Entries made before this are balances. It still moves no money — HQ raises the voucher.',
      },
    ],
  },
  {
    version: '1.5.0',
    date: '21 Aug 2026',
    items: [
      {
        title: 'A rental says landlord and tenant',
        body: 'On a Rent deal the two sides are now named the way you name them — landlord and tenant, not vendor and purchaser. Sale details, the parties you add, the SST line and the contact pickers all follow the deal type.',
      },
      {
        title: 'Learn the app, on your computer too',
        body: 'The guided walkthroughs now run on the desktop layout — pointing at the sidebar, the side panels and the document pane you actually have there, instead of the phone screens.',
      },
      {
        title: 'Invoices raised on your branch’s own book',
        body: 'Where a branch issues an invoice from its own accounting system, HQ can now record it against the sale with the document attached, and you can view or download it here like any other. A row with nothing attached yet says so, instead of offering a button that opens nothing.',
      },
      {
        title: 'Smaller things',
        body: 'On a computer, opening a document saves it straight to Downloads instead of offering a phone-style share sheet — Share is unchanged on your phone. And a slow moment on the server no longer bounces you back to the login screen: you are signed out only when your session has really ended.',
      },
    ],
  },
  {
    version: '1.4.0',
    date: '19 Aug 2026',
    items: [
      {
        title: 'Use CIDAS on your computer',
        body: 'Open CIDAS on a laptop and you get a full desktop layout — a sidebar carrying every module, tables instead of stacked cards, side panels instead of pop-up sheets. Your phone is unchanged.',
      },
      {
        title: 'Balance To on a sale',
        body: 'A new Balance tab records who the money left in the Client Account belongs to once the invoice is settled — the landlord or vendor, the tenant or purchaser, you, or a law firm — with the bank account it goes to and a note for finance. It moves no money: HQ still raises the voucher.',
      },
      {
        title: 'Commissions tab shows only your lines',
        body: 'A sale’s Commissions tab no longer lists everybody else’s share. Your full deal sheet is unchanged on Earnings.',
      },
      {
        title: 'Earnest deposit as a fixed amount',
        body: 'On an OTR or an ALR you can now quote the earnest deposit as a fixed RM sum instead of months of rental. Forms issued before this are untouched.',
      },
      {
        title: 'Smaller things',
        body: 'Agent photos open full size on desktop, and CID Copilot AI opens beside the page instead of over it.',
      },
    ],
  },
  {
    version: '1.3.1',
    date: '18 Aug 2026',
    items: [
      { title: 'Learn the app', body: 'Guided walkthroughs on More that practise a real deal end to end, on made-up data.' },
      { title: 'New home screen', body: 'Your money, your deals and what needs doing, in that order.' },
      { title: 'Notifications', body: 'The app now asks before turning on push, and says what it will send.' },
      { title: 'Booking forms', body: 'A form can no longer be issued naming only one side of the deal.' },
    ],
  },
  {
    version: '1.2.0',
    date: '11 Aug 2026',
    items: [
      { title: 'Co-share', body: 'Ask to bring a buyer to another agent’s listing, and sign the fee split in the app.' },
      { title: 'Sale notes', body: 'Comments on a sale with formatting, mentions and attachments.' },
      { title: 'Team performance', body: 'Production for your downline and your branch.' },
      { title: 'Listings', body: 'Publish your own listing — no approval step.' },
    ],
  },
];

/** The version the notes above describe — must equal package.json's. */
/** The What's new page. Not /whats-new: that is the folder the clips are served from. */
export const WHATS_NEW_PATH = '/updates';

export const LATEST_VERSION = RELEASES[0]?.version || null;

/**
 * Releases the agent has not been shown yet.
 *
 * `seen` is the version they last acknowledged. No `seen` means a fresh install:
 * nothing to announce, because there is no "before" for them to have missed.
 * A `seen` this list has never heard of (a downgrade, or a build that shipped
 * without notes) announces the current release only, rather than replaying the
 * entire history at somebody who has been using the app for months.
 */
export function unseenSince(seen) {
  if (!seen) return [];
  if (seen === LATEST_VERSION) return [];
  const i = RELEASES.findIndex((r) => r.version === seen);
  return i === -1 ? RELEASES.slice(0, 1) : RELEASES.slice(0, i);
}
